import React, { Suspense } from 'react';

import { useAppContext } from '../../../context/app-context';
import { SubPage, TabType } from '../../../context/tab-manager-context';
import {
  AlertBoardV2,
  AlertManager,
  LimitsV2,
  ManagePage,
  PortalMenuProvider,
  TabContentByMode,
  ViewControls,
} from '../../../routes';
import { Loading } from '../../loading/loading';

import { ViewType } from '@controlrooms/models';

// Lazy load heavy components - only load when VIEWS type tab is active
const LazyViewControls = ViewControls;
const LazyPortalMenuProvider = PortalMenuProvider;
const LazyTabContentByMode = TabContentByMode;

// Lazy load tab type specific components
const LazyAlertBoardV2 = AlertBoardV2;
const LazyAlertManager = AlertManager;
const LazyLimitPage = LimitsV2;
const LazyManagePage = ManagePage;

interface TabContentV3Props {
  tab: {
    id: string;
    type: TabType;
    viewType?: ViewType;
    subPage?: SubPage;
    viewId?: string;
  };
}

/**
 * TabContentV3 - Routes content based on tab type (ALERTS, VIEWS, MANAGE)
 * Memoized with custom comparison to prevent re-render when tab properties haven't changed
 */
export const TabContentV3 = React.memo<TabContentV3Props>(
  ({ tab }) => {
    const { isTenantSwitching } = useAppContext();

    if (isTenantSwitching) {
      return <Loading overlay small={true} />;
    }

    if (tab.type === 'ALERT_BOARD') {
      return (
        <Suspense fallback={<Loading overlay small={true} />}>
          <LazyAlertBoardV2 />
        </Suspense>
      );
    }

    if (tab.type === 'ALERT_MANAGER') {
      return (
        <Suspense fallback={<Loading overlay small={true} />}>
          <LazyAlertManager />
        </Suspense>
      );
    }

    if (tab.type === 'LIMIT_MANAGER') {
      return (
        <Suspense fallback={<Loading overlay small={true} />}>
          <LazyLimitPage key={tab.id} />
        </Suspense>
      );
    }

    if (tab.type === 'MANAGE') {
      return (
        <Suspense fallback={<Loading overlay small={true} />}>
          <LazyManagePage key={tab.id} />
        </Suspense>
      );
    }

    // VIEWS type - render with PortalMenuProvider and ViewControls (lazy loaded)
    return (
      <Suspense fallback={<Loading overlay small={true} />}>
        <LazyPortalMenuProvider>
          <Suspense fallback={<Loading overlay small={true} />}>
            <LazyViewControls />
          </Suspense>
          <Suspense fallback={<Loading overlay small={true} />}>
            <LazyTabContentByMode viewType={tab.viewType || ViewType.MONITOR} />
          </Suspense>
        </LazyPortalMenuProvider>
      </Suspense>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if tab properties actually change
    return (
      prevProps.tab.id === nextProps.tab.id &&
      prevProps.tab.type === nextProps.tab.type &&
      prevProps.tab.viewType === nextProps.tab.viewType &&
      prevProps.tab.subPage === nextProps.tab.subPage &&
      prevProps.tab.viewId === nextProps.tab.viewId
    );
  },
);

TabContentV3.displayName = 'TabContentV3';
